<script setup lang="ts">
    import { ref, computed, onMounted } from 'vue'
    import { boolCompare } from '@common/Common'

    import type { AlternativeReadModel } from '@Evaluation/Estimations/Domain/readmodels'

    // ---------------- props / emits ----------------

    const props = defineProps<{
        CorrelationId: string
        Title: string
        Alternatives: AlternativeReadModel[]
        SelectedAlternativesIds: number[]
    }>()

    const emit = defineEmits<{
        (e: 'confirm', payload: {
            CorrelationId: string
            SelectedIds: number[]
        }): void
        (e: 'close'): void
    }>()

    // ---------------- local state ----------------

    const alternatives = ref<AlternativeVM[] | null>(null)

    // ---------------- computed ----------------

    const alternativesView = computed<AlternativeVM[] | undefined>(() =>
    {
        return alternatives.value?.slice().sort((a, b) =>
            boolCompare(a.Included, b.Included)
        )
    })

    // ---------------- lifecycle ----------------

    onMounted(() =>
    {
        const selectedIds = props.SelectedAlternativesIds ?? []

        alternatives.value = props.Alternatives.map(
            a => new AlternativeVM(
                a,
                selectedIds.indexOf(a.AlternativeId) > -1
            )
        )
    })

    // ---------------- actions ----------------

    function confirm()
    {
        if (!alternatives.value)
            return

        emit('confirm', {
            CorrelationId: props.CorrelationId,
            SelectedIds: alternatives.value
                .filter(a => a.Included === true)
                .map(a => a.AlternativeId)
        })
    }

    function close()
    {
        emit('close')
    }

    // ---------------- runtime VM ----------------

    class AlternativeVM
    {
        AlternativeId: number
        Name: string
        Included: boolean

        constructor(a: AlternativeReadModel, included: boolean)
        {
            this.AlternativeId = a.AlternativeId
            this.Name = a.Name
            this.Included = included
        }

        toggleIncluded()
        {
            this.Included = !this.Included
        }
    }
</script>


<template>

    <div class="modal-mask">
        <div class="modal-wrapper">
            <div class="modal-dialog modal-xl modal-dialog-scrollable">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title">{{Title}}</h5>
                        <button @click="close" type="button" class="close">&times;</button>
                    </div>

                    <div class="modal-body">

                        <div class="list-group">
                            <button type="button" v-for="a in alternativesView" :key="a.AlternativeId" @click="a.toggleIncluded()" class="list-group-item list-group-item-action">
                                <span v-if="a.Included" class="fa-regular fa-square-check"></span>
                                <span v-if="!a.Included" class="fa-regular fa-square"></span>
                                {{a.Name}}
                            </button>
                        </div>
                    </div>

                    <div class="modal-footer">
                        <button @click="confirm" type="button" class="btn btn-primary">Confirm</button>
                        <button @click="close" type="button" class="btn btn-outline-secondary">Cancel</button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>